import { useState, useEffect, useCallback, useMemo } from "react";

const AmazonCognitoIdentity = require("amazon-cognito-identity-js");

const defaultUserPoolId = process.env.REACT_APP_USER_POOL_ID;
const defaultClientId =   process.env.REACT_APP_USER_POOL_CLIENT_ID;

export const useAuth = (userPoolId = defaultUserPoolId, clientId = defaultClientId) => {
  const userPool = useMemo(() => {
    return new AmazonCognitoIdentity.CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId:   clientId
    });
  }, [userPoolId, clientId]);
  const [user, setUser] = useState(userPool.getCurrentUser());
  window.loginUser = user;
  const [userDisplayName, setName] = useState("");
  const [sessionError, setSessionError] = useState(null);

  const [challengeUsername, setChallengeUsername] = useState(null);
  const [loginState, setLoginState] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [loginCognitoUser, setLoginCognitoUser] = useState(null);

  const [mfaSecret, setMfaSecret] = useState(null);
  const [setupMfaError, setMfaError] = useState(null);

  const loginCallbacks = {
    // 成功時
    onSuccess: () => {
      setLoginState(null);
      setUser(userPool.getCurrentUser());
    },
    // パスワード設定に失敗した場合
    //   本パスワード設定に失敗した場合も呼ばれる
    onFailure: (error) => {
      setLoginState(null);
      setLoginError(error);
    },
    // 本パスワード未登録の場合のパスワード登録呼び出し処理
    newPasswordRequired: () => {
      setLoginState("newPasswordRequired");
    },
    totpRequired: () => {
      setLoginState("totpRequired");
    }
  };

  const login = useCallback((username, password) => {
    const userData = { Username: username, Pool: userPool };
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
    setChallengeUsername(username);
    setLoginError(null);
    setLoginCognitoUser(cognitoUser);
    const authenticationData = { Username: username, Password: password };
    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(authenticationData);
    cognitoUser.authenticateUser(authenticationDetails, loginCallbacks);
  }, [userPool]);

  const completeNewPassword = useCallback((newPassword) => {
    if (!loginCognitoUser) {
      return;
    }
    setLoginError(null);
    loginCognitoUser.completeNewPasswordChallenge(newPassword, {}, loginCallbacks);
  }, [loginCognitoUser]);

  const sendMFACode = (onetimePassword) => {
    if (!loginCognitoUser) {
      return;
    }
    setLoginError(null);
    loginCognitoUser.sendMFACode(onetimePassword, loginCallbacks, "SOFTWARE_TOKEN_MFA");
  };

  const getSession = useCallback(() => {
    setSessionError(null);
    return new Promise((resolve, reject) => {
      if (!user) {
        reject(new Error("ログインしていません"));
        return;
      }
      user.getSession((error, result) => {
        if (error) {
          setSessionError(error);
          reject(error);
          return;
        }
        resolve(result);
      });
    });
  }, [user]);

  const getAccessToken = async () => {
    const session = getSession();
    return session.getIdToken().getJwtToken();
  };
  const logout = useCallback(() => {
    if (!user) {
      return;
    }
    user.signOut();
    setUser(null);
  }, [user]);

  // 他のwindowでログアウトされたときに、この画面もログアウト状態にする
  const storageKey = "WonderOrder.auth";
  useEffect(() => {
    const callback = ({ key, newValue }) => {
      if ((key === storageKey) && (newValue == null) && (user != null)) {
        setUser(null);
      }
    };
    window.addEventListener("storage", callback);
    return () => window.removeEventListener("storage", callback);
  }, []);
  // このwindowでログイン、ログアウトしたときに、localStorage変更して他のwindowに伝える
  useEffect(() => {
    if (user == null) {
      window.localStorage.removeItem(storageKey);
      setName(null);
      return;
    }
    window.localStorage.setItem(storageKey, user.username);
    getSession().then((session) => setName(session.getIdToken().payload.name));
  }, [user]);

  const changePassword = (currentPassword, newPassword) => {
    return getSession().then(() => {
      return new Promise((resolve, reject) => {
        user.changePassword(currentPassword, newPassword, (error) => {
          if (error) {
            reject(error);
            return;
          }
          resolve();
        });
      });
    });
  };

  const startSetupMfa = () => {
    getSession().then(() => {
      user.associateSoftwareToken({
        onFailure: (error) => {
          setMfaError(error);
        },
        associateSecretCode: (secret) => {
          setMfaSecret(secret);
        }
      });
    });
  };

  const verifySoftwareToken = (oneTimeToken) => {
    // cognitoUser.verifySoftwareToken(result[getValue], 'My TOTP device', _this);
    return getSession().then(() => {
      return new Promise((resolve, reject) => {
        user.verifySoftwareToken(oneTimeToken, `${user.username} device`, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (error) => {
            reject(error);
          }
        });
      });
    });
  };

  const cancelSetupMfa = () => {
    setMfaSecret(null);
  };

  return {
    challengeUsername,
    changePassword,
    login,
    completeNewPassword,
    sendMFACode,
    loginState,
    loginError,
    userDisplayName,
    getSession,
    getAccessToken,
    sessionError,
    logout,
    user,
    startSetupMfa,
    cancelSetupMfa,
    mfaSecret,
    verifySoftwareToken,
    setupMfaError
  };
};